import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";

import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../../hooks/useXoraia";

import { isEqual } from "lodash";

import { generateSide } from "../../../lib/generateSide";
import { IXHeaderProps } from "../../../utils/interfaces/common/XHeader.interfaces";
import { Menu as MenuIcon } from "@mui/icons-material";
import {
  MAIN_COLOR,
  SECONDARY_COLOR,
} from "../../../utils/constants/common/utils";
import { generateRadius } from "../../../lib/generateRadius";
import { generateBorder } from "../../../lib/generateBorder";
import { TCoreXElement } from "../../../utils/types/core/CoreXelement";
import ViewShoppingCartBtn from "../../core/dynamic/ViewShoppingCartBtn";
import { useXPathValue } from "../../../hooks/useXPath";
import ViewWishListBtn from "../../core/dynamic/ViewWishListBtn";
import { useSetRecoilState } from "recoil";
import { USE_ACTION_DRAWER } from "../../../hooks/useActionDrawer";
import { MENU_LINKS } from "../../../utils/constants/core/MENU_LINKS";
import XSearch from "../../core/XSearch";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XHeaderCore: TCoreXElement<IXHeaderProps> = React.memo(
  ({ props, devProps }) => {
    const setDrawer = useSetRecoilState(USE_ACTION_DRAWER);

    const xPath = useXPathValue();

    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env, Link } = useXoraiaValue();

    const {
      // GENERAL
      headerMode = "static",
      position,
      boxShadow,
      bgColor = MAIN_COLOR,
      blur = 0,
      collapseAt = "md",
      menuPosition = "flex-start",
      height = "64px",
      backgroundMode = "FILL",
      containerMode = "CENTER",

      // logo
      // TODO ADD XORAIA LOGO HERE
      logo = "https://dev.xoraia.com/_next/image?url=%2Fimages%2FLOGO_XORAIA_WHITE.png&w=96&q=75",
      logoHeight = "44px",
      LogoText,
      logoTextColor,
      logoTextFontSize,
      logoTextFontFamily,
      logoTextFontWeight,

      // content
      contentColor = "#FFFFFF90",
      contentFontSize = "16px",
      contentFontWeight = 500,
      contentHoverColor = "#FFFFFF",
      contentLetterSpacing = "0px",
      contentTextTransform = "capitalize",

      // icons
      iconColor = "#FFFFFF",
      iconBgColor,
      iconBorder,
      iconRadius,
      iconHoverColor = "#000000",
      iconHoverBgColor = "#FFFFFF",
      iconHoverBorder,

      // BADGE
      badgeColor = SECONDARY_COLOR,
      badgeTextColor = "#FFFFFF",

      // AVATAR
      // avatarBgColor = "#000000",
      // avatarTextColor = "#FFFFFF",
    } = props;

    const collapse = (value: any) => {
      if (env !== "dev") {
        return { [collapseAt]: value };
      }

      if (
        collapseAt === "md" &&
        (breakpoint === "mobile" || breakpoint === "tablet")
      ) {
        return { xs: value };
      }

      if (collapseAt === "sm" && breakpoint === "mobile") {
        return { xs: value };
      }

      return { [collapseAt]: value };
    };

    const hasCollapsed =
      (collapseAt === "sm" && breakpoint === "mobile") ||
      (collapseAt === "md" &&
        (breakpoint === "tablet" || breakpoint === "mobile"));

    console.log("HAS_COLLAPSED =>", hasCollapsed);

    const content = (
      <Toolbar
        disableGutters
        sx={{
          height,
          minHeight: "auto !important",
          "& img": {
            mr: 1,
            height: logoHeight,
            "&.defaultImg": {
              display: () => ({
                ...collapse("flex"),
                xs: "none",
              }),
            },
            "&.smImg": {
              display: () => ({
                ...collapse("none"),
                xs: "flex",
              }),
            },
          },
        }}
      >
        {!!logo && (
          <Link href={env !== "dev" ? "/" : undefined}>
            <img
              src={logo}
              alt={LogoText ?? "Logo"}
              loading="lazy"
              className="defaultImg"
            />
          </Link>
        )}

        {!!LogoText && (
          <Typography
            className="LOGO_TEXT_1"
            variant="h4"
            noWrap
            component={Link}
            href={env !== "dev" ? "/" : undefined}
            sx={{
              mr: 2,
              display: () => ({ ...collapse("flex"), xs: "none" }),
              fontFamily: logoTextFontFamily ?? "inherit",
              fontWeight: logoTextFontWeight,
              color: logoTextColor ?? "inherit",
              fontSize: logoTextFontSize ?? 24,
              textDecoration: "none",
            }}
          >
            {LogoText}
          </Typography>
        )}

        <Box
          sx={{
            display: () => ({ ...collapse("none"), xs: "flex" }),
            position: "relative",
          }}
        >
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={() => {
              setDrawer("MENU");
            }}
            color="inherit"
          >
            <MenuIcon
              sx={{
                color: contentColor,
                "&:hover": { color: contentHoverColor },
              }}
            />
          </IconButton>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          className="LOGO_TEXT_2"
          flex={hasCollapsed ? 1 : 0}
        >
          {!!logo && (
            <Link href={env !== "dev" ? "/" : undefined}>
              <img
                src={logo}
                alt={LogoText ?? "Logo"}
                loading="lazy"
                className="smImg"
              />
            </Link>
          )}
          {!!LogoText && (
            <Typography
              variant="h5"
              noWrap
              component={Link}
              href={env !== "dev" ? "/" : undefined}
              sx={{
                mr: 2,
                display: () => ({ ...collapse("none"), xs: "flex" }),
                fontFamily: logoTextFontFamily ?? "inherit",
                fontWeight: logoTextFontWeight,
                color: logoTextColor ?? "inherit",
                fontSize: logoTextFontSize ?? 24,
                textDecoration: "none",
              }}
            >
              {LogoText}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          justifyContent={menuPosition}
          sx={{
            flexGrow: 1,
            display: () => ({ ...collapse("flex"), xs: "none" }),
          }}
        >
          {MENU_LINKS.map((page) => {
            const isActived = xPath === page.path;

            return (
              <Button
                disableRipple
                LinkComponent={Link}
                href={env !== "dev" ? page.path : undefined}
                key={page.path}
                sx={{
                  my: 2,
                  display: "block",
                  textTransform: contentTextTransform,
                  letterSpacing: contentLetterSpacing,
                  color: isActived
                    ? contentHoverColor ?? contentColor
                    : contentColor,
                  fontSize: contentFontSize,
                  fontWeight: contentFontWeight,

                  "&:hover": {
                    color: contentHoverColor,
                  },
                }}
              >
                {page.label}
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          color="white"
          position={"relative"}
          sx={{
            "& .MuiBadge-badge": {
              bgcolor: badgeColor,
              color: badgeTextColor,
            },
            "& .MuiIconButton-root": {
              color: iconColor,
              bgcolor: iconBgColor,
              transition: "all linear 0.2s",
              borderRadius: generateRadius(iconRadius, "50%"),
              border: generateBorder(iconBorder),

              "&:hover": {
                color: iconHoverColor,
                bgcolor: iconHoverBgColor,
                border: generateBorder(iconHoverBorder),
              },
            },
          }}
        >
          <XSearch />
          <ViewShoppingCartBtn />
          <ViewWishListBtn />

          {/* // todo implement account later */}
          {/* <Tooltip title="Mon Compte">
            <IconButton
              onClick={() => {
                setDrawer("ACCOUNT");
              }}
              sx={{ p: 0 }}
            >
              <Avatar
                sx={{
                  bgcolor: avatarBgColor,
                  color: avatarTextColor,
                  fontSize: 14,
                  fontWeight: 800,
                }}
              >
                <AccountCircleOutlined />
              </Avatar>
            </IconButton>
          </Tooltip> */}
        </Stack>
      </Toolbar>
    );

    const Centerer: React.FC<{ children: React.ReactNode }> = ({
      children,
    }) => {
      if (containerMode === "NONE") {
        return <React.Fragment>{children}</React.Fragment>;
      }

      return (
        <Container maxWidth={containerMode === "CENTER" ? "lg" : false}>
          {children}
        </Container>
      );
    };

    const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
      return (
        <AppBar
          position={
            headerMode === "fixed" && env === "dev" ? "sticky" : headerMode
          }
          {...(env === "dev" && devProps)}
          sx={{
            bgcolor: bgColor,
            ...generateSide("position", position),
            backdropFilter: blur > 0 ? `blur(${blur}px)` : undefined,
            boxShadow: boxShadow ?? "none",
          }}
        >
          {children}
        </AppBar>
      );
    };

    if (backgroundMode === "CONTAINED") {
      return (
        <Centerer>
          <Wrapper>
            <Container maxWidth={false} sx={{ px: "10px !important" }}>
              {content}
            </Container>
          </Wrapper>
        </Centerer>
      );
    }

    return (
      <Wrapper>
        <Centerer>{content}</Centerer>
      </Wrapper>
    );
  },
  compareProps
);

export default XHeaderCore;
